import logger from '@/lib/logger';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Fire, Barbell, Brain, ArrowRight, Play, Wind, ShareNetwork
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import ShareStreakModal from '@/components/ShareStreakModal';
import StatsGrid from '@/components/dashboard/StatsGrid';
import MoodWidget from '@/components/dashboard/MoodWidget';
import RecentActivity from '@/components/dashboard/RecentActivity';
import OnboardingTour from '@/components/OnboardingTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOOD_LABELS = ['Terrible', 'Bad', 'Okay', 'Good', 'Great'];
const CHART_TICK = { fill: '#71717A', fontSize: 11 };
const CHART_TOOLTIP_STYLE = { background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, color: '#fff', fontFamily: 'DM Sans' };
const CHART_CURSOR = { fill: 'rgba(255,255,255,0.05)' };
const BAR_RADIUS = [2, 2, 0, 0];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [moodValue, setMoodValue] = useState(0);
  const [energyValue, setEnergyValue] = useState(0);
  const [moodLogged, setMoodLogged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, recRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/workouts/recommended`),
      ]);
      setStats(statsRes.data);
      setRecommended(recRes.data);
      if (statsRes.data.today_mood) {
        setMoodValue(statsRes.data.today_mood.mood);
        setEnergyValue(statsRes.data.today_mood.energy);
        setMoodLogged(true);
      }
    } catch (err) {
      logger.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const logMood = useCallback(async () => {
    try {
      await axios.post(`${API}/mood/log`, { mood: moodValue, energy: energyValue, notes: '' });
      setMoodLogged(true);
    } catch (err) {
      logger.error('Mood log error:', err);
    }
  }, [moodValue, energyValue]);

  const startRecommended = useCallback(() => {
    if (recommended?.exercises?.length) {
      navigate('/workouts/player', { state: { exercises: recommended.exercises } });
    }
  }, [recommended, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const streak = stats?.streak || {};
  const weekData = (stats?.week_activity || []).map(d => ({
    ...d,
    day: new Date(d.date + 'T12:00:00').toLocaleDateString('en', { weekday: 'short' }).toUpperCase()
  }));
  const moodHistory = (stats?.mood_history || []).map(m => ({
    ...m,
    day: new Date(m.date + 'T12:00:00').toLocaleDateString('en', { weekday: 'short' }).toUpperCase(),
    moodLabel: MOOD_LABELS[(m.mood || 1) - 1]
  }));
  const recentCompletions = stats?.recent_completions || [];
  const isNewUser = (streak.total_workouts || 0) === 0;

  return (
    <div data-testid="dashboard-page">
      {/* Greeting */}
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Dashboard</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
          {isNewUser ? 'WELCOME, ' : 'WELCOME BACK, '}
          <span className="text-[#FF3B30]">{user?.name?.toUpperCase()}</span>
        </h1>
      </div>

      {/* First-time Welcome Hero */}
      {isNewUser && (
        <div className="tactical-card p-8 mb-6 border-[#FF3B30]/20 relative overflow-hidden" data-testid="welcome-hero">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#FF3B30] rounded-full blur-[160px] opacity-10" />
          <div className="relative z-10">
            <Fire weight="fill" className="w-10 h-10 text-[#FF3B30] mb-4" />
            <h2 className="text-3xl font-black tracking-tighter mb-2">READY TO START YOUR JOURNEY?</h2>
            <p className="text-[#A1A1AA] max-w-lg mb-6">
              Complete your first workout to start building your streak. We've picked the perfect routine for your level — just hit Start Now below!
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/workouts" className="btn-primary px-6 py-3 flex items-center gap-2" data-testid="welcome-browse-workouts">
                <Barbell weight="bold" className="w-5 h-5" /> Browse Workouts
              </Link>
              <Link to="/ai-plan" className="btn-secondary px-6 py-3 flex items-center gap-2" data-testid="welcome-ai-plan">
                <Brain weight="bold" className="w-5 h-5" /> Generate AI Plan
              </Link>
            </div>
          </div>
        </div>
      )}

      <StatsGrid streak={streak} stats={stats} onShareClick={() => setShowShareModal(true)} />

      {/* Today's Recommended Workout */}
      {recommended?.exercises?.length > 0 && (
        <div className="tactical-card p-5 sm:p-6 mb-6 border-[#FF3B30]/30 pulse-glow" data-testid="recommended-workout">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <div className="label-tactical text-[#FF3B30] mb-1">Workout of the Day</div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">{recommended.title}</h3>
              <p className="text-sm text-[#A1A1AA] mt-1">{recommended.exercises.length} exercises | ~{recommended.estimated_minutes} min</p>
            </div>
            <button onClick={startRecommended} className="btn-primary px-6 py-3 flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="start-recommended-button">
              <Play weight="fill" className="w-5 h-5" /> Start Now
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {recommended.exercises.map((ex) => (
              <div key={ex.id || ex.name} className="flex-shrink-0 bg-[#1C1C1E] border border-white/5 px-3 py-1.5 text-xs sm:text-sm">
                <span className="text-white font-medium">{ex.name}</span>
                <span className="text-[#71717A] ml-1.5">{ex.sets}x{ex.reps}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chart + Mood Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 tactical-card p-6" data-testid="week-chart">
          <h3 className="text-xl font-bold tracking-tight mb-4">WEEKLY ACTIVITY</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekData}>
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={CHART_TICK} />
                <YAxis hide />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  cursor={CHART_CURSOR}
                  formatter={(value) => [`${value} min`, 'Activity']}
                />
                <Bar dataKey="minutes" fill="#FF3B30" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <MoodWidget
          moodLogged={moodLogged}
          moodValue={moodValue}
          energyValue={energyValue}
          moodHistory={moodHistory}
          onMoodSelect={setMoodValue}
          onEnergySelect={setEnergyValue}
          onLogMood={logMood}
        />
      </div>

      <RecentActivity completions={recentCompletions} />

      {/* Share Streak Banner */}
      {(streak.current_streak || 0) > 0 && (
        <button
          onClick={() => setShowShareModal(true)}
          className="w-full tactical-card p-5 mt-4 hover:border-[#FF3B30]/30 transition-all group flex items-center justify-between"
          data-testid="share-streak-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FF3B30]/10 border border-[#FF3B30]/20 flex items-center justify-center group-hover:bg-[#FF3B30]/20 transition-colors">
              <ShareNetwork weight="bold" className="w-6 h-6 text-[#FF3B30]" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold tracking-tight">SHARE YOUR {streak.current_streak}-DAY STREAK</h3>
              <p className="text-sm text-[#A1A1AA]">Download a shareable card for Instagram, Twitter, or stories</p>
            </div>
          </div>
          <ArrowRight weight="bold" className="w-5 h-5 text-[#FF3B30] group-hover:translate-x-1 transition-transform" />
        </button>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Link to="/workouts" className="tactical-card p-6 hover:border-white/20 transition-all group" data-testid="quick-workouts">
          <Barbell weight="bold" className="w-8 h-8 text-[#FF3B30] mb-3" />
          <h3 className="text-lg font-bold tracking-tight mb-1">BROWSE WORKOUTS</h3>
          <p className="text-sm text-[#A1A1AA] mb-3">Explore exercises by body part</p>
          <span className="text-[#FF3B30] text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
            Start <ArrowRight weight="bold" className="w-4 h-4" />
          </span>
        </Link>
        <Link to="/ai-plan" className="tactical-card p-6 hover:border-white/20 transition-all group" data-testid="quick-ai-plan">
          <Brain weight="bold" className="w-8 h-8 text-[#007AFF] mb-3" />
          <h3 className="text-lg font-bold tracking-tight mb-1">AI WORKOUT PLAN</h3>
          <p className="text-sm text-[#A1A1AA] mb-3">Get a personalized training plan</p>
          <span className="text-[#007AFF] text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
            Generate <ArrowRight weight="bold" className="w-4 h-4" />
          </span>
        </Link>
        <Link to="/stress-reset" className="tactical-card p-6 hover:border-white/20 transition-all group" data-testid="quick-stress-reset">
          <Wind weight="bold" className="w-8 h-8 text-[#34C759] mb-3" />
          <h3 className="text-lg font-bold tracking-tight mb-1">STRESS RESET</h3>
          <p className="text-sm text-[#A1A1AA] mb-3">Quick breathing & mobility sessions</p>
          <span className="text-[#34C759] text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
            Reset <ArrowRight weight="bold" className="w-4 h-4" />
          </span>
        </Link>
      </div>

      <ShareStreakModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} />
      <OnboardingTour />
    </div>
  );
}
